import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiV2 from '../../../services/apiV2';
import { V2RunPresentation } from '../../../pages/v2/student/RunPresentation';
import { PathwayWorkspace } from './PathwayWorkspace';
import { AppShell } from '../../layout/AppShell';
import { studentV2NavItems } from '../../layout/v2NavItems';
import { usePacedDay } from '../../../services/pacedDay';
import { PacedStudent } from './PacedStudent';

/** Stable student entry point; each runner retains its own response and draft identity. */
export function ClassroomSession() {
  const { sectionId } = useParams();
  const pacing = usePacedDay(Number(sectionId));
  useEffect(() => { try { sessionStorage.setItem('student-classroom-section', String(sectionId)); } catch { /* optional navigation memory */ } }, [sectionId]);
  const [section, setSection] = useState<any>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    setSection(null);
    const load = async () => {
      try {
        const {data} = await apiV2.get('/my/units/');
        if (live) {
          const found = (data.pathways || []).find((p: any) => p.section_id === Number(sectionId));
          setSection(found || null);
          setError(found ? '' : 'This classroom is not available to your account.');
        }
      } catch { if (live) setError('Connection interrupted. Keeping your current activity open; reconnecting…'); }
      finally { if (live) timer = setTimeout(load, 5000); }
    };
    void load();
    return () => { live = false; clearTimeout(timer); };
  }, [sectionId, retry]);
  const match = section?.today?.run_href?.match(/^\/v2\/run\/(\d+)$/);
  const run = match && !section?.daily_plan?.live_required ? Number(match[1]) : null;
  const phase = pacing.data?.phases?.find((p: any) => p.id === pacing.data.current_phase_id);
  const protectedTest = pacing.data?.student?.protection?.active;
  const showRunner = !pacing.data?.enabled || protectedTest || (phase?.kind === 'work' && phase.work_mode === 'lesson');
  return <AppShell role="student" title="Classroom" navItems={studentV2NavItems('dashboard', Number(sectionId), section?.configuration)}>
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-xl font-medium">{section?.section_name || 'Your classroom'}</h2><p className="text-sm text-muted">Stay here for the next activity. Your teacher controls the shared lesson.</p></div><Link className="text-brand underline" to={`/v2/student?section=${sectionId}`}>Leave classroom view</Link></div>
    {error && <p role="alert">{error}</p>}
    {!section && !error && <p role="status">Loading your classroom…</p>}
    <PacedStudent sectionId={Number(sectionId)} state={pacing} />
    {run && <div hidden={!showRunner}><V2RunPresentation key={run} classroomRunId={run} embeddedClassroom /></div>}
    {(!run || !showRunner) && section && (!pacing.data?.enabled || phase?.kind === 'work') && <PathwayWorkspace pathways={[section]} view="today" embedded refresh={() => setRetry(v => v + 1)} />}
  </AppShell>;
}
